// Shared through the DOM because the stream controller also has a lazy bundle.
export function confidentialStorageIsAllowed(): boolean {
  return document.documentElement.dataset.elronSessionEnded !== "true";
}

const logoutMarker = "elron:session-ended-at";
const localKeys = ["elron:ticket-chat:", "htmx-history-cache"];
const sessionKeys = ["ticketChatStream:", "ticketChatStreamCancelled:", "app:debug-log", "elronTicketActionRunAttempt", "elron:tickets:list-scroll-snapshot"];

function purge(storage: Storage, prefixes: string[]): void {
  for (const key of Object.keys(storage)) {
    if (prefixes.some((prefix) => key.startsWith(prefix))) storage.removeItem(key);
  }
}

export function endConfidentialSession(broadcast = true): void {
  document.documentElement.dataset.elronSessionEnded = "true";
  // Stop pending writes before clearing storage or disconnecting controllers.
  window.dispatchEvent(new Event("elron:session-ended"));
  try { purge(window.localStorage, localKeys); } catch { /* Storage can be disabled. */ }
  try { purge(window.sessionStorage, sessionKeys); } catch { /* Storage can be disabled. */ }
  if (broadcast) {
    try { window.localStorage.setItem(logoutMarker, `${Date.now()}:${Math.random()}`); } catch { /* Best effort cross-tab notification. */ }
  }
}

export function initializeSessionPrivacy(): void {
  window.addEventListener("elron:session-invalidated", () => endConfidentialSession());
  const identity = document.querySelector<HTMLMetaElement>('meta[name="elron-session-user"]');
  if (identity && !identity.content) endConfidentialSession();
  let initialMarker: string | null = null;
  try { initialMarker = window.localStorage.getItem(logoutMarker); } catch { /* Storage can be disabled. */ }
  document.addEventListener("htmx:beforeRequest", (event) => {
    const detail = (event as CustomEvent<{ requestConfig?: { path?: string } }>).detail;
    if (detail?.requestConfig?.path?.split("?")[0] === "/DeleteSession") endConfidentialSession();
  });
  document.addEventListener("htmx:beforeOnLoad", (event) => {
    const xhr = (event as CustomEvent<{ xhr?: XMLHttpRequest }>).detail?.xhr;
    if (xhr?.getResponseHeader("X-Elron-Session-Ended") === "true") {
      event.preventDefault();
      endConfidentialSession();
      window.location.replace(xhr.getResponseHeader("HX-Redirect") || "/NewSession");
    } else if (!confidentialStorageIsAllowed() && identity?.content && !xhr?.getResponseHeader("HX-Redirect")) {
      // A fragment requested before logout must not restore private DOM.
      event.preventDefault();
    }
  });
  const leavePrivatePage = (): void => {
    endConfidentialSession(false);
    if (identity?.content) {
      document.body.replaceChildren();
      window.location.replace("/NewSession");
    }
  };
  window.addEventListener("storage", (event) => {
    if (event.key === logoutMarker && event.newValue !== initialMarker) leavePrivatePage();
  });
  window.addEventListener("popstate", (event) => {
    if (!identity?.content || !event.state?.htmx) return;
    // HTMX cache hits skip the server and history misses use a separate XHR
    // transport. Revalidate private HTMX history before either restores its DOM.
    // Other entries keep their own ticket/chat navigation handlers.
    event.stopImmediatePropagation();
    document.body.replaceChildren();
    window.location.reload();
  }, { capture: true });
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted || !identity?.content) return;
    // BFCache can outlive both cookies and cross-tab storage availability.
    // Require a fresh authenticated document before showing private content.
    endConfidentialSession(false);
    document.body.replaceChildren();
    window.location.reload();
  });
}
